import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { SubHubGrid, ContentSection, FaqBlock, buildFaqLd, buildBreadcrumbLd, ResultCard, ReservedResultsArea, inputCls } from "@/components/age-calculator/shared";
import { AnalyzeButton, AnalyzingOverlay, WhatsAppShareButton, useAnalyzeGate, GaugeDial } from "@/components/age-calculator/premium";

const TITLE = "Biological Age Calculator: Health, Heart & BMI Age Fit Test";
const DESC = "Is your body older or younger than your chronological years? Calculate your metabolic, biological, and heart age based on fitness inputs.";

const FAQS = [
  { q: "What is biological age?", a: "Biological age is an estimate of how old your body 'behaves' based on cardiovascular fitness, BMI and lifestyle — as opposed to your chronological age which just counts calendar years." },
  { q: "How does BMI affect biological age?", a: "A BMI outside the healthy 18.5–24.9 range is correlated with earlier onset of cardiovascular disease, diabetes and joint problems. Every 5-point step above 25 typically adds 1–2 years of biological aging." },
  { q: "How is resting heart rate factored in?", a: "A low resting heart rate signals cardiovascular fitness. Every 5 BPM below 60 subtracts about a year; every 10 BPM above 75 adds one. Athletes routinely have RHR of 45–55." },
  { q: "Can I actually lower my biological age?", a: "Yes. Consistent aerobic exercise, resistance training, sleep, and Mediterranean-style diet can shift epigenetic age markers by 2–3 years within 12 months, per DNA methylation studies." },
];

export const Route = createFileRoute("/age-calculator/biological-health-bmi")({
  head: () => leafHead({ path: "/age-calculator/biological-health-bmi", title: TITLE, description: DESC, scripts: [buildFaqLd(FAQS), buildBreadcrumbLd([{ name: "Home", url: "https://drhint.com/" }, { name: "Age Calculator", url: "https://drhint.com/age-calculator" }, { name: "Biological & Health Age", url: "https://drhint.com/age-calculator/biological-health-bmi" }])] }),
  component: Page,
});

function Page() {
  const [age, setAge] = useState(35);
  const [heightCm, setHeightCm] = useState(170);
  const [weightKg, setWeightKg] = useState(70);
  const [rhr, setRhr] = useState(70);
  const [active, setActive] = useState(true);
  const [smoker, setSmoker] = useState(false);
  const [sleepGood, setSleepGood] = useState(true);
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const bump = () => reset();

  const r = useMemo(() => {
    const m = heightCm / 100;
    const bmi = weightKg / (m * m);
    let adj = 0;
    if (bmi >= 30) adj += 4;
    else if (bmi >= 25) adj += 2;
    else if (bmi < 18.5) adj += 2;
    if (rhr < 60) adj -= 1.5;
    if (rhr > 80) adj += 2;
    else if (rhr > 75) adj += 1;
    if (active) adj -= 2; else adj += 2;
    if (smoker) adj += 5;
    if (sleepGood) adj -= 1; else adj += 2;
    const bio = Math.max(15, Math.round(age + adj));
    const status = bio < age - 1 ? "Younger than your years 🎉" : bio > age + 1 ? "Older than your years ⚠️" : "Right on target ✅";
    const score = Math.max(0, Math.min(100, Math.round(100 - adj * 6)));
    const dialStatus: "younger" | "equal" | "older" = bio < age - 1 ? "younger" : bio > age + 1 ? "older" : "equal";
    return { bmi: bmi.toFixed(1), bio, status, score, gap: bio - age, dialStatus };
  }, [age, heightCm, weightKg, rhr, active, smoker, sleepGood]);

  const shareMsg = `💪 My Biological Age Fit Test\n\n📊 Bio age: ${r.bio} yrs (vs chronological ${age})\n🏋️ Fit score: ${r.score}/100\n❤️‍🔥 Status: ${r.status.replace(/[^\w\s]/g, '').trim()}\n\nCheck yours on https://drhint.com/age-calculator/biological-health-bmi`;

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Biological & Health Age
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">{DESC}</p>
      </section>
      <section className="mx-auto max-w-5xl px-4">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="flex flex-col gap-1"><span className="text-sm font-semibold">Chronological Age</span>
              <input type="number" min={15} max={100} value={age} onChange={(e) => { setAge(parseInt(e.target.value) || 0); bump(); }} className={inputCls()} /></label>
            <label className="flex flex-col gap-1"><span className="text-sm font-semibold">Height (cm)</span>
              <input type="number" min={120} max={220} value={heightCm} onChange={(e) => { setHeightCm(parseInt(e.target.value) || 0); bump(); }} className={inputCls()} /></label>
            <label className="flex flex-col gap-1"><span className="text-sm font-semibold">Weight (kg)</span>
              <input type="number" min={30} max={200} value={weightKg} onChange={(e) => { setWeightKg(parseInt(e.target.value) || 0); bump(); }} className={inputCls()} /></label>
            <label className="flex flex-col gap-1"><span className="text-sm font-semibold">Resting Heart Rate (BPM)</span>
              <input type="number" min={35} max={130} value={rhr} onChange={(e) => { setRhr(parseInt(e.target.value) || 0); bump(); }} className={inputCls()} /></label>
          </div>
          <div className="mt-4 flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={active} onChange={(e) => { setActive(e.target.checked); bump(); }} /> Active (150+ min/week)</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sleepGood} onChange={(e) => { setSleepGood(e.target.checked); bump(); }} /> Sleeps 7–9 hrs</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={smoker} onChange={(e) => { setSmoker(e.target.checked); bump(); }} /> Smoker</label>
          </div>
          <AnalyzeButton onClick={trigger} disabled={analyzing} label="Run Fit Test" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={280}>
            {revealed ? (
              <>
                <div className="grid gap-6 sm:grid-cols-2 items-center">
                  <div className="flex justify-center">
                    <GaugeDial value={r.bio} max={100} label="Biological Age" sub={r.dialStatus === "younger" ? "younger" : r.dialStatus === "older" ? "older" : "on target"} status={r.dialStatus} />
                  </div>
                  <div className="text-center sm:text-left">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Verdict</div>
                    <div className="text-2xl md:text-3xl font-black mt-1">{r.status}</div>
                    <div className="mt-3 text-sm text-muted-foreground">Chronological age <span className="font-semibold text-foreground">{age}</span> · Biological gap <span className="font-semibold text-foreground">{r.gap >= 0 ? `+${r.gap}` : r.gap}</span></div>
                  </div>
                </div>
                <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="BMI" value={r.bmi} sub={parseFloat(r.bmi) < 18.5 ? "Underweight" : parseFloat(r.bmi) < 25 ? "Healthy" : parseFloat(r.bmi) < 30 ? "Overweight" : "Obese"} />
                  <ResultCard label="Biological Age" value={`${r.bio} yrs`} sub={r.gap === 0 ? "Match" : r.gap > 0 ? `+${r.gap} vs chronological` : `${r.gap} vs chronological`} />
                  <ResultCard label="Fitness Status" value={r.status} />
                  <ResultCard label="Fit Score" value={`${r.score}/100`} />
                </div>
                <WhatsAppShareButton message={shareMsg} label="Share My Fit Score" />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Fill your details and tap Run Fit Test.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>
      <ContentSection title="Biological vs. chronological age — what the numbers mean">
        <p>
          Two people can share the same birthday and yet have wildly different <strong>biological ages</strong>. One
          spends evenings walking and eating a Mediterranean diet; the other smokes and skips sleep. On paper both are
          40; in cardiovascular terms one is 34 and the other is 51.
        </p>
        <p>
          Our tool blends four scientifically validated markers: <strong>BMI</strong> (a strong long-term mortality
          proxy), <strong>resting heart rate</strong> (cardiovascular fitness), <strong>weekly activity minutes</strong>
          and <strong>sleep quality</strong>. It's not a lab-grade epigenetic test, but the same variables drive most
          clinical risk models — and they're the ones you can actually change.
        </p>
        <h3>What to do with the result</h3>
        <ul>
          <li><strong>Younger than your years</strong> — keep the streak. Add resistance training if you haven't already.</li>
          <li><strong>On target</strong> — small consistency wins compound; try one 30-minute walk more per week.</li>
          <li><strong>Older than your years</strong> — the biggest single lever is quitting smoking, followed by adding 150 minutes of aerobic activity per week.</li>
        </ul>
        <p>
          Curious how your pet compares? Convert cat and dog years with our{" "}
          <Link to="/age-calculator/pet-dog-cat" className="text-primary underline">Pet Age Calculator</Link>, or plan your
          long-term horizon on the <Link to="/age-calculator/retirement-pension" className="text-primary underline">Retirement & Pension Calculator</Link>.
        </p>
      </ContentSection>
      <FaqBlock faqs={FAQS} />
      <SubHubGrid excludePath="/age-calculator/biological-health-bmi" />
    </Layout>
  );
}